import {
  CustomAction,
  DataTable,
  DataTableColumn,
  DataTableOptions,
  DataTableSkeleton,
  DataType,
  FilterType,
  RowCustomAction
} from '@globaleris/ui.library'
import { Typography } from '@material-ui/core'
import { CloudDownload, Edit, Undo } from '@material-ui/icons'
import React from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { EnumTipoCPrecio } from '../../../../api/oData/enums/enumTipoCPrecio'
import { enumToSelectOptionArray } from '../../../../helpers'
import { useConfirmationDialog } from '../../../../hooks'
import { ConfirmationDialogs } from '../../../../model/enums/confirmationDialogs'
import { getCustomerDomain } from '../../../../redux'
import { PriceModelQryDataTable } from '../../quotes.model'

interface PriceListTableProps {
  data: PriceModelQryDataTable[] | undefined
  quoteIsDraft: boolean
  isLoading: boolean
  onRowManualEditionClick?: (tableRow: PriceModelQryDataTable) => void
  onRowUndoManualEditionClick?: (tableRow: PriceModelQryDataTable) => void
  onDownloadClick?: () => void
}

export const PriceListTable: React.FC<PriceListTableProps> = ({
  data,
  quoteIsDraft,
  isLoading,
  onRowManualEditionClick,
  onRowUndoManualEditionClick,
  onDownloadClick
}) => {
  const [t] = useTranslation(['globalerisLibrary', 'quotes', 'enums'])
  const history = useHistory()
  const { getConfirmation, closeConfirmation } = useConfirmationDialog()

  // #region Handlers Starts
  const handleUndoManualEditConfirmation = (tableRow: PriceModelQryDataTable) => {
    getConfirmation(ConfirmationDialogs.UNDO_QUOTE_PRICELIST_MANUAL_EDIT).then(async () => {
      onRowUndoManualEditionClick && onRowUndoManualEditionClick(tableRow)
      closeConfirmation()
    })
  }
  // #endregion Handlers Ends

  // #region Functions Starts
  const getRowCustomActions = () => {
    const actions: RowCustomAction[] = []
    if (quoteIsDraft) {
      onRowManualEditionClick &&
        actions.push({
          icon: <Edit fontSize="small" />,
          tooltip: t('quotes:components.priceListTable.dataTable.tooltips.manualEdit'),
          onClick: onRowManualEditionClick
        })
      onRowUndoManualEditionClick &&
        actions.push({
          icon: <Undo fontSize="small" />,
          tooltip: t('quotes:components.priceListTable.dataTable.tooltips.undoManualEdit'),
          onClick: handleUndoManualEditConfirmation,
          visibleIf: 'lgManual'
        })
    }

    return actions
  }

  // #endregion Functions Ends

  // #region Form Definition Starts
  // #endregion Form Definition Ends

  // #region DataTable Config Starts
  const columns: DataTableColumn[] = [
    {
      name: 'id',
      display: false
    },
    {
      name: 'code',
      label: t('quotes:components.priceListTable.dataTable.columns.code'),
      type: DataType.TEXT,
      columnWidth: 90
    },
    {
      name: 'description',
      label: t('quotes:components.priceListTable.dataTable.columns.description'),
      type: DataType.TEXT,
      columnWidth: 120
    },
    {
      name: 'priceType',
      label: t('quotes:components.priceListTable.dataTable.columns.priceType'),
      type: DataType.SELECT,
      selectOptions: enumToSelectOptionArray(EnumTipoCPrecio, 'enums:EnumTipoCPrecio'),
      columnWidth: 60
    },
    {
      name: 'expression',
      label: t('quotes:components.priceListTable.dataTable.columns.formula'),
      type: DataType.TEXT,
      columnWidth: 160
    },
    {
      name: 'currentValuesString',
      label: t('quotes:components.priceListTable.dataTable.columns.todayValue'),
      type: DataType.ARRAY,
      hideForResponsive: true,
      columnWidth: 120
    },
    {
      name: 'lgManual',
      label: t('quotes:components.priceListTable.dataTable.columns.manualEdit'),
      type: DataType.BOOLEAN,
      columnWidth: 60
    }
  ]

  const getMainToolbarCustomActions = (): CustomAction[] => {
    const actions: CustomAction[] = []
    if (onDownloadClick) {
      actions.push({
        icon: <CloudDownload fontSize="small" />,
        tooltip: t('quotes:actions.downloadPriceList'),
        onClick: () => onDownloadClick()
      })
    }
    return actions
  }

  const options: DataTableOptions = {
    i18n: t('globalerisLibrary:dataTable', { returnObjects: true }),
    search: true,
    filterType: FilterType.ADVANCED,
    elevation: 1,
    download: false,
    canEdit: false,
    canDelete: false,
    canInsert: false,
    pagination: true,
    analytics: {
      customer: getCustomerDomain(),
      view: t('quotes:components.priceListTable.dataTable.analytics')
    },
    rowCustomActions: getRowCustomActions(),
    mainToolbarCustomActions: getMainToolbarCustomActions()
  }
  // #endregion DataTable Config Ends

  // #region Hooks Starts
  // #endregion Hooks Ends
  return (
    <>
      {isLoading ? (
        <DataTableSkeleton rows={5} elevation={0} />
      ) : (
        <DataTable
          title={
            <Typography variant="h6" component="div">
              {t('quotes:components.priceListTable.dataTable.title')}
            </Typography>
          }
          columns={columns}
          options={options}
          data={data}
        />
      )}
    </>
  )
}
